
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Image, TrendingUp, TrendingDown, MessageSquare, Star, Camera, BarChart, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ScrollArea } from '@/components/ui/scroll-area';

export interface Suggestion {
  id: string;
  message: string;
  icon: 'image' | 'trending-up' | 'trending-down' | 'message-square' | 'star' | 'camera' | 'chart-bar';
  action?: {
    label: string;
    link: string;
  };
  severity: 'low' | 'medium' | 'high';
}

interface PerformanceSuggestionsProps {
  suggestions: Suggestion[];
  maxSuggestions?: number;
  onDismiss?: (id: string) => void;
}

const PerformanceSuggestions: React.FC<PerformanceSuggestionsProps> = ({ 
  suggestions, 
  maxSuggestions = 5,
  onDismiss 
}) => {
  const displayedSuggestions = suggestions.slice(0, maxSuggestions);
  
  const getIcon = (iconName: Suggestion['icon']) => {
    switch (iconName) {
      case 'image':
        return <Image className="h-5 w-5" />;
      case 'trending-up':
        return <TrendingUp className="h-5 w-5" />;
      case 'trending-down':
        return <TrendingDown className="h-5 w-5" />;
      case 'message-square':
        return <MessageSquare className="h-5 w-5" />;
      case 'star':
        return <Star className="h-5 w-5" />;
      case 'camera':
        return <Camera className="h-5 w-5" />;
      case 'chart-bar':
        return <BarChart className="h-5 w-5" />;
      default:
        return <TrendingUp className="h-5 w-5" />;
    }
  };
  
  const getSeverityColor = (severity: Suggestion['severity']) => {
    switch (severity) {
      case 'low':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'medium':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'high':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };
  
  if (displayedSuggestions.length === 0) {
    return null;
  }
  
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">🧠 Suggestions to Improve Your Performance</h3>
      <ScrollArea className={`${displayedSuggestions.length > 3 ? 'max-h-[400px]' : ''}`}>
        <div className="space-y-3 pr-4">
          {displayedSuggestions.map((suggestion) => (
            <Card 
              key={suggestion.id}
              className={`border ${getSeverityColor(suggestion.severity)}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 rounded-full bg-white p-1.5 shadow-sm">
                    {getIcon(suggestion.icon)}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{suggestion.message}</p>
                    {suggestion.action && (
                      <Button
                        variant="link"
                        className="px-0 h-auto py-1"
                        asChild
                      >
                        <Link to={suggestion.action.link}>
                          {suggestion.action.label}
                        </Link>
                      </Button>
                    )}
                  </div>
                  {onDismiss && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-7 w-7" 
                      onClick={() => onDismiss(suggestion.id)}
                    >
                      <X className="h-4 w-4" />
                      <span className="sr-only">Dismiss</span>
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};

export default PerformanceSuggestions;
